// The analysis host (P2.5) — the main-thread side of analysis-worker.entry.ts.
//
// Same shape and the same reasoning as `render-host.ts`: the worker is an implementation detail,
// and everything above this file sees a promise and a buffer.
//
// **One worker, not yet a pool.** Architecture §5 says the analysis stages "run as a pool", and
// they will — M3 has five stages and two decks, which is when a pool starts earning its
// scheduling. M2 loads one track into one deck at a time, so a pool today would be a queue with
// one item in it and a lifecycle to get wrong. The seam that matters is this class's interface,
// which does not change when the thing behind it grows.

import {
  computeWaveformMipmapLayout,
  type WaveformMipmapLayout,
} from '../../platform/types/waveform-mipmap';
import {
  applyStageComplete,
  pendingStages,
  type AnalyseTrackMessage,
  type AnalysisStages,
  type AnalysisWorkerResponse,
} from '../../platform/types/analysis';
import type { TrackMemorySpec } from '../../platform/types/track-memory';

/**
 * Where the analysis worker's bundle lives — the same stable URL in dev and production.
 *
 * Not `new Worker(new URL('./analysis-worker.entry.ts', import.meta.url))`, which is the idiomatic
 * Vite spelling and is wrong here for the reason P0.8 learned the hard way with the render worker:
 * that form makes Vite emit its *own* chunk, so the bundle guard and the payload budget would both
 * be measuring a copy nobody loads. A guard pointed at a shadow is not a guard. This URL is the one
 * `vite.bundles.ts` builds, guards and emits.
 */
export const ANALYSIS_WORKER_URL = '/analysis-worker.js';

export interface TrackAnalysis {
  /** The mipmaps. Shared, so P2.6 can read it from wherever it ends up drawing. */
  readonly mipmaps: SharedArrayBuffer;
  readonly layout: WaveformMipmapLayout;
  /** Measured in the worker: FR-11's budget with a number attached. */
  readonly elapsedMillis: number;
  /**
   * Every Architecture §5 stage and what became of it (P3.1).
   *
   * **Present from the first update, not assembled at the end.** FR-11's progressive availability is
   * the point: the waveform is on screen while tempo and key are still running, so a consumer holds
   * a record where `mipmaps` is `done` and the rest are `pending`, and re-renders as each settles.
   * A stage that has not run and a stage that produced nothing are different states here on
   * purpose — see `StageOutcome`.
   */
  readonly stages: AnalysisStages;
}

export interface AnalyseHooks {
  /**
   * Called every time a stage settles, with the record as it stands.
   *
   * A callback rather than an async iterator, because the consumer is a React hook that wants to
   * call `setState` and nothing here should own a queue. Called with a fresh object each time, so
   * an identity check is enough to re-render.
   */
  onStage?: (analysis: TrackAnalysis) => void;
}

export interface AnalyseRequest {
  /** The deck's track memory. Shared, never transferred — see platform/types/analysis.ts. */
  readonly trackMemory: SharedArrayBuffer;
  readonly track: TrackMemorySpec;
  readonly sampleRateHz: number;
}

export class AnalysisHost {
  #worker: Worker | null = null;

  /**
   * Summarises a track. The returned buffer is readable *before* the promise settles — levels
   * flip their ready bits as they complete, and `FRAMES_DONE` advances throughout — which is the
   * whole reason the host allocates it rather than waiting for the worker to hand one back.
   *
   * Starting a second analysis **terminates the first**. A user who drops a new track onto a deck
   * has said what they want; finishing the summary of a track that is no longer loaded would burn
   * a core to fill a buffer nothing will read. Terminating also removes the class of bug where two
   * runs' responses interleave and the wrong one resolves.
   */
  analyse(
    request: AnalyseRequest,
    hooks: AnalyseHooks = {},
  ): {
    readonly mipmaps: SharedArrayBuffer;
    readonly layout: WaveformMipmapLayout;
    readonly done: Promise<TrackAnalysis>;
  } {
    this.dispose();

    const layout = computeWaveformMipmapLayout({
      frameCount: request.track.frameCount,
      sampleRateHz: request.sampleRateHz,
    });
    const mipmaps = new SharedArrayBuffer(layout.totalBytes);

    const worker = new Worker(ANALYSIS_WORKER_URL, {
      type: 'module',
      name: 'synapse-analysis',
    });
    this.#worker = worker;

    // Accumulated across messages rather than assembled at the end, because the whole point of
    // P3.1 is that a consumer can read a half-finished record. `stages` is replaced rather than
    // mutated on each update so a React consumer sees a new object and re-renders.
    let stages = pendingStages();
    let mipmapMillis = 0;

    const snapshot = (): TrackAnalysis => ({
      mipmaps,
      layout,
      elapsedMillis: mipmapMillis,
      stages,
    });

    const done = new Promise<TrackAnalysis>((resolve, reject) => {
      worker.addEventListener(
        'message',
        (event: MessageEvent<AnalysisWorkerResponse>) => {
          const response = event.data;

          switch (response.type) {
            case 'stage-complete':
              // The fold lives in `platform/types/analysis.ts` rather than here because it is pure
              // — no worker, no SharedArrayBuffer — so it runs under Node and is tested. This class
              // cannot be: it exists to construct a `Worker`. Keeping the part that could be wrong
              // on the testable side of that line is the same split `mipmap.ts` and `pipeline.ts`
              // already have from the entry that hosts them.
              stages = applyStageComplete(
                stages,
                response.stage,
                response.value,
                response.elapsedMillis,
              );
              // FR-11's number, kept where P2.5's callers already look for it. The mipmap stage is
              // the one with a published budget, so it is the one whose duration is surfaced.
              if (response.stage === 'mipmaps') {
                mipmapMillis = response.elapsedMillis;
              }
              hooks.onStage?.(snapshot());
              break;

            case 'stage-skipped':
              stages = {
                ...stages,
                [response.stage]: { status: 'skipped', reason: response.reason },
              };
              hooks.onStage?.(snapshot());
              break;

            case 'analysis-complete':
              resolve(snapshot());
              break;

            case 'analysis-failed':
              // **The earlier stages survive.** A key-detection bug must not throw away a waveform
              // that is already drawn and a track the user can already play, so the record is
              // published one last time with the failure recorded against the stage that caused it
              // — and only then does the promise reject.
              stages = {
                ...stages,
                [response.stage]: { status: 'failed', reason: response.reason },
              };
              hooks.onStage?.(snapshot());
              reject(
                new Error(
                  `Analysis failed during the ${response.stage} stage: ${response.reason}`,
                ),
              );
              break;
          }
        },
      );

      // A worker that fails to *load* never sends a message, so without this the promise would
      // hang forever and the UI would sit on "Analysing…" with no way out. The likeliest cause is
      // a 404 on the URL above, which is exactly the failure a stale deploy produces.
      worker.addEventListener('error', (event: ErrorEvent) => {
        reject(
          new Error(
            `The analysis worker failed to start (${event.message || ANALYSIS_WORKER_URL}).`,
          ),
        );
      });
    });

    const message: AnalyseTrackMessage = {
      type: 'analyse-track',
      trackMemory: request.trackMemory,
      track: request.track,
      frameCount: request.track.frameCount,
      sampleRateHz: request.sampleRateHz,
      mipmapBuffer: mipmaps,
    };
    // No transfer list. Every buffer here is shared; a transfer list would detach the deck's own
    // track memory and silence the audio thread mid-track.
    worker.postMessage(message);

    return { mipmaps, layout, done };
  }

  dispose(): void {
    this.#worker?.terminate();
    this.#worker = null;
  }
}
